// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { useTenantStore } from "@/providers/TenantContextProvider";
import { GET_EVENT_MESSAGING_CONFIG, GET_EVENT_MESSAGING_CONFIG_AUTHOR, } from "@/queries/GetEventMessagingConfig";
import { parseEventMessagingConfig } from "@/services/messaging";
import { roleContext, useMessagingReadRole } from "./useMessagingReadRole";
/**
 * The event's messaging configuration from `messaging_event_config`, read
 * with messaging-config-read. An event without a row has an empty `config`
 * and a null `storedConfig`. `canRead` is false without the role.
 */
export var useEventMessagingConfig = function (electionEventId, options) {
    var _a;
    if (options === void 0) { options = {}; }
    var tenantId = useTenantStore()[0];
    var role = useMessagingReadRole();
    var _b = useQuery(GET_EVENT_MESSAGING_CONFIG, {
        variables: { tenantId: tenantId, electionEventId: electionEventId },
        skip: options.skip || !tenantId || !electionEventId || !role,
        context: roleContext(role),
    }), data = _b.data, loading = _b.loading, error = _b.error, refetch = _b.refetch;
    var row = (_a = data === null || data === void 0 ? void 0 : data.sequent_backend_messaging_event_config) === null || _a === void 0 ? void 0 : _a[0];
    var stored = row === null || row === void 0 ? void 0 : row.config;
    var config = useMemo(function () { return parseEventMessagingConfig(stored !== null && stored !== void 0 ? stored : null); }, [stored]);
    // An event without a row isn't the same as one whose configuration is empty:
    // the backend sends notices by email and SMS only without a row.
    var storedConfig = row ? config : null;
    return { config: config, storedConfig: storedConfig, loading: loading, error: error, refetch: refetch, canRead: !!role };
};
/**
 * Who last wrote the event's configuration (`updated_by`), or undefined when
 * it can't be read. Separate from the configuration, so its failure never
 * hides the tab.
 */
export var useEventMessagingConfigAuthor = function (electionEventId) {
    var _a, _b;
    var tenantId = useTenantStore()[0];
    var role = useMessagingReadRole();
    var _c = useQuery(GET_EVENT_MESSAGING_CONFIG_AUTHOR, {
        variables: { tenantId: tenantId, electionEventId: electionEventId },
        skip: !tenantId || !electionEventId || !role,
        context: roleContext(role),
    }), data = _c.data, refetch = _c.refetch;
    return { updatedBy: (_b = (_a = data === null || data === void 0 ? void 0 : data.sequent_backend_messaging_event_config) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.updated_by, refetch: refetch };
};
